import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { ADD_EOD_INCIDENT, DELETE_EOD_INCIDENT } from '../../../graphql/production-mutations'
import toast from 'react-hot-toast'

type Severity = 'low' | 'medium' | 'high'

export interface EodIncident {
  id: number
  eod_session_id: number
  description: string
  severity: Severity
  created_at: string
}

interface IncidentsSectionProps {
  session: { id: number; incidents: EodIncident[] }
  canLog: boolean
  onUpdate: (updated: { incidents: EodIncident[] }) => void
}

const SEVERITY_STYLES: Record<Severity, string> = {
  low: 'bg-blue-100 text-blue-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-red-100 text-red-800',
}

const IncidentsSection = ({ session, canLog, onUpdate }: IncidentsSectionProps) => {
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState<Severity>('low')
  const [formOpen, setFormOpen] = useState(false)

  const [addIncident, { loading: adding }] = useMutation(ADD_EOD_INCIDENT, {
    onCompleted: (data) => {
      onUpdate({ incidents: [...session.incidents, data.addEodIncident] })
      setDescription('')
      setSeverity('low')
      setFormOpen(false)
      toast.success('Incident added')
    },
    onError: (e) => toast.error(e.message),
  })

  const [deleteIncident] = useMutation(DELETE_EOD_INCIDENT, {
    onCompleted: (_data, clientOptions) => {
      const deletedId = clientOptions?.variables?.id
      onUpdate({ incidents: session.incidents.filter((i) => i.id !== deletedId) })
      toast.success('Incident removed')
    },
    onError: (e) => toast.error(e.message),
  })

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault()
    if (!description.trim()) { toast.error('Enter a description'); return }
    addIncident({ variables: { session_id: session.id, description: description.trim(), severity } })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {session.incidents.length === 0 ? 'No incidents logged.' : `${session.incidents.length} incident${session.incidents.length > 1 ? 's' : ''} logged.`}
        </p>
        {canLog && !formOpen && (
          <button
            onClick={() => setFormOpen(true)}
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
          >
            <PlusIcon className="h-4 w-4" />
            Add incident
          </button>
        )}
      </div>

      <ul className="space-y-2">
        {session.incidents.map((incident) => (
          <li key={incident.id} className="flex items-start gap-3 rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
            <span className={`mt-0.5 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium shrink-0 ${SEVERITY_STYLES[incident.severity]}`}>
              {incident.severity}
            </span>
            <p className="flex-1 text-sm text-gray-800">{incident.description}</p>
            {canLog && (
              <button
                onClick={() => deleteIncident({ variables: { id: incident.id } })}
                className="text-gray-300 hover:text-red-500 shrink-0"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </li>
        ))}
      </ul>

      {formOpen && canLog && (
        <form onSubmit={handleAdd} className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="input w-full resize-none"
              placeholder="What happened?"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
            <div className="flex gap-2">
              {(['low', 'medium', 'high'] as Severity[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSeverity(s)}
                  className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                    severity === s
                      ? SEVERITY_STYLES[s] + ' border-transparent'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setFormOpen(false); setDescription(''); setSeverity('low') }}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={adding}
              className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-500 disabled:opacity-50">
              {adding ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

export default IncidentsSection
